import { Drawer, List } from 'antd';
import classNames from 'classnames';
import { AkamLogoCheckIcon, AkamLogoTextIcon, InstagramIcon, TelegramIcon } from 'icons';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';

import { useAppDispatch, useAppSelector } from 'store';

import { openAuthModal } from 'modules/auth';
import { globalConst } from 'modules/global';

interface IProps {
  open: boolean;
  onClose: () => void;
  navItems: { label?: string; href: string; isNew?: boolean }[];
}

const MobileDrawer: React.FC<IProps> = ({ open, onClose, navItems }) => {
  const dispatch = useAppDispatch();
  const { pathname, locale = 'uz', push, query, asPath } = useRouter();
  const user = useAppSelector(state => state.user.phone);
  const t = globalConst.extraTranslation.footer[locale as 'uz' | 'ru'];
  const tHeader = globalConst.extraTranslation.header[locale as 'uz' | 'ru'];

  const handleLanguageChange = (loc: string) => () => {
    if (locale === loc) return;
    push({ pathname, query }, asPath, { locale: loc });
  };

  return (
    <Drawer
      footer={
        <>
          <div className="mr-5">
            <h3 className="text-md mb-4 font-medium">{t.contact_us}</h3>
            <div className="space-y-3 text-sm">
              <div>
                <p className="text-xs text-gray-400">{t.contact}: </p>
                {/* eslint-disable-next-line i18next/no-literal-string */}
                <div className='flex flex-col gap-1'>
                  <Link href="tel:+998942508101" target="_blank" className="!font-medium !text-black">
                    +998 94 250 81 01
                  </Link>
                  <Link href="tel:+998917979749" target="_blank" className="!font-medium !text-black">
                    +998 91 797 97 49
                  </Link>
                </div>
              </div>
              <div>
                <p className="text-xs text-gray-400">{t.address}:</p>
                {/* eslint-disable-next-line i18next/no-literal-string */}
                <span className="!font-medium !text-black"> Toshkent, ko'cha. Аxmad Donish, 20А 100180</span>
              </div>
            </div>
          </div>

          {/* Social */}
          <div className="my-5">
            <h3 className="mb-4 text-sm font-medium text-gray-500">{t.social}</h3>
            <div className="flex items-center gap-2">
              <Link href="https://www.instagram.com/akam.uz/" target="_blank">
                <span className="text-black">
                  {/* eslint-disable-next-line i18next/no-literal-string */}
                  <InstagramIcon />
                </span>
              </Link>
              <Link href={`${process.env.NEXT_PUBLIC_TELEGRAM}`} target="_blank">
                <span className="text-black">
                  {/* eslint-disable-next-line i18next/no-literal-string */}
                  <TelegramIcon width="21" />
                </span>
              </Link>
            </div>
          </div>
        </>
      }
      closeIcon={false}
      width="21rem"
      title={
        <Link href="/">
          <div className="flex items-center space-x-2 text-xl font-bold text-black">
            <AkamLogoCheckIcon />
            <AkamLogoTextIcon />
          </div>
        </Link>
      }
      placement="left"
      onClose={onClose}
      open={open}
    >
      <List
        dataSource={navItems}
        renderItem={item => {
          const isActive = item.href === '/' ? pathname === '/' : pathname.startsWith(item.href);

          return (
            <List.Item>
              <Link
                href={item.href}
                className={classNames(
                  `
                  flex w-full items-center text-base`,
                  {
                    '!font-medium !text-green-500': isActive,
                    '!text-gray-800': !isActive
                  }
                )}
                onClick={onClose}
              >
                {item.label}
              </Link>
            </List.Item>
          );
        }}
      />

      {!user ? (
        <button
          onClick={() => dispatch(openAuthModal())}
          className="mt-6 flex w-full cursor-pointer items-center justify-center rounded-lg !bg-green-500 px-7 py-2.5 text-sm font-medium !text-white transition-transform duration-200 hover:scale-105"
          aria-label={tHeader.royxatdan_otish}
        >
          {tHeader.royxatdan_otish}
        </button>
      ) : null}
      <div className="mt-7 flex items-center gap-2">
        {['uz', 'ru'].map(loc => (
          <div
            key={loc}
            onClick={handleLanguageChange(loc)}
            className={classNames(
              'flex h-11 w-11 cursor-pointer items-center justify-center rounded-lg border transition-transform duration-200 hover:scale-105',
              {
                'border-gray-200': locale !== loc,
                'border-gray-100 bg-gray-100': locale === loc
              }
            )}
          >
            <div className="w-4.5 relative h-3">
              <Image
                fill
                src={loc === 'ru' ? '/images/ru-flag.webp' : '/images/uz-flag.webp'}
                alt="Uz Flag"
                title="O'zbek tili"
              />
            </div>
          </div>
        ))}
      </div>
    </Drawer>
  );
};

export default MobileDrawer;
